import { MfaFactor, experience } from '@logto/schemas';
import { Route, Routes, BrowserRouter } from 'react-router-dom';

import { handleSearchParametersData } from '@/shared/utils/search-parameters';

import AppLayout from './Layout/AppLayout';
import AppBoundary from './Providers/AppBoundary';
import CaptchaContextProvider from './Providers/CaptchaContextProvider';
import LoadingLayerProvider from './Providers/LoadingLayerProvider';
import PageContextProvider from './Providers/PageContextProvider';
// RestoID: settings provider that shows a branded error+retry instead of a blank
// page when the sign-in-experience settings fail to load (replaces upstream).
import SettingsProvider from '../custom/components/RestoSettingsProvider';
import UserInteractionContextProvider from './Providers/UserInteractionContextProvider';
import DevelopmentTenantNotification from './containers/DevelopmentTenantNotification';
import Callback from './pages/Callback';
import Consent from '../custom/pages/Consent';
import Continue from '../custom/pages/Continue';
import Device from '../custom/pages/Device';
import DeviceSuccess from '../custom/pages/DeviceSuccess';
import DirectSignIn from './pages/DirectSignIn';
import DocumentPage from '../custom/pages/DocumentPage';
import ErrorPage from '../custom/pages/ErrorPage';
import ForgotPassword from '../custom/pages/ForgotPassword';
import IdentifierRegister from '../custom/pages/IdentifierRegister';
import IdentifierSignIn from '../custom/pages/IdentifierSignIn';
import MfaBinding from '../custom/pages/MfaBinding';
import BackupCodeBinding from '../custom/pages/MfaBackupCodeBinding';
import EmailMfaBinding from '../custom/pages/MfaEmailBinding';
import PhoneMfaBinding from '../custom/pages/MfaPhoneBinding';
import TotpBinding from '../custom/pages/MfaTotpBinding';
import WebAuthnBinding from '../custom/pages/MfaWebAuthnBinding';
import MfaOnboarding from '../custom/pages/MfaOnboarding';
import MfaVerification from '../custom/pages/MfaVerification';
import BackupCodeVerification from '../custom/pages/MfaBackupCodeVerification';
import EmailVerificationCode from '../custom/pages/MfaEmailVerification';
import PhoneVerificationCode from '../custom/pages/MfaPhoneVerification';
import TotpVerification from '../custom/pages/MfaTotpVerification';
import WebAuthnVerification from '../custom/pages/MfaWebAuthnVerification';
import OneTimeToken from './pages/OneTimeToken';
import OneTimeTokenErrorPage from '../custom/pages/OneTimeTokenError';
import PasskeySetup from '../custom/pages/PasskeySetup';
import Register from '../custom/pages/Register';
import RegisterPassword from '../custom/pages/RegisterPassword';
import ResetPassword from '../custom/pages/ResetPassword';
import ResetPasswordLanding from '../custom/pages/ResetPasswordLanding';
import SignIn from '../custom/pages/SignIn';
import SignInPasskeyVerification from '../custom/pages/SignInPasskeyVerification';
import SignInPassword from '../custom/pages/SignInPassword';
import SignInVerificationMethods from '../custom/pages/SignInVerificationMethods';
import SingleSignOnConnectors from '../custom/pages/SingleSignOnConnectors';
import SingleSignOnEmail from '../custom/pages/SingleSignOnEmail';
import SingleSignOnLanding from '../custom/pages/SingleSignOnLanding';
import SocialLanding from '../custom/pages/SocialLanding';
import SocialLinkAccount from '../custom/pages/SocialLinkAccount';
import SocialSignInWebCallback from './pages/SocialSignInWebCallback';
import SwitchAccount from '../custom/pages/SwitchAccount';
import VerificationCode from '../custom/pages/VerificationCode';
import { UserMfaFlow } from './types';
import 'overlayscrollbars/overlayscrollbars.css';
import './shared/scss/normalized.scss';
import './scss/overlayscrollbars.scss';
// RestoID theme overlay (custom layer — re-skins the pristine upstream experience).
import '../custom/theme/restoid.css';
// eslint-disable-next-line import/no-unassigned-import -- side-effect: mounts RestoID page chrome
import '../custom/chrome/restoid-chrome';

handleSearchParametersData();

const App = () => {
  return (
    <BrowserRouter>
      <PageContextProvider>
        <SettingsProvider>
          <UserInteractionContextProvider>
            <CaptchaContextProvider>
              <DevelopmentTenantNotification />
              <AppBoundary>
                <Routes>
                  <Route element={<LoadingLayerProvider />}>
                    <Route path="callback/:connectorId" element={<Callback />} />
                    <Route
                      path="callback/social/:connectorId"
                      element={<SocialSignInWebCallback />}
                    />
                    <Route path="direct/:method/:target?" element={<DirectSignIn />} />
                    <Route path={experience.routes.oneTimeToken} element={<OneTimeToken />} />

                    {/* RestoID standalone legal document pages — own chrome, outside AppLayout */}
                    <Route path="privacy" element={<DocumentPage docId="privacy" />} />
                    <Route path="terms" element={<DocumentPage docId="terms" />} />

                    <Route element={<AppLayout />}>
                      <Route
                        path={`${experience.routes.oneTimeToken}/error`}
                        element={<OneTimeTokenErrorPage />}
                      />
                      <Route path={experience.routes.switchAccount} element={<SwitchAccount />} />
                      <Route
                        path="unknown-session"
                        element={<ErrorPage message="error.invalid_session" />}
                      />
                      <Route
                        path={experience.routes.accountSuspended}
                        element={
                          <ErrorPage
                            isNavbarHidden
                            title="error.account_suspended"
                            message="error.account_suspended_description"
                            primaryAction={{
                              title: 'description.back_to_sign_in',
                              to: `/${experience.routes.signIn}`,
                              replace: true,
                            }}
                          />
                        }
                      />

                      {/* Sign-in */}
                      <Route path={experience.routes.signIn}>
                        <Route index element={<SignIn />} />
                        <Route path="password" element={<SignInPassword />} />
                        <Route path="passkey" element={<SignInPasskeyVerification />} />
                        <Route
                          path="verification-methods"
                          element={<SignInVerificationMethods />}
                        />
                      </Route>

                      {/* Create passkey for sign-in */}
                      <Route path="create-passkey" element={<PasskeySetup />} />

                      {/* Register */}
                      <Route path={experience.routes.register}>
                        <Route index element={<Register />} />
                        <Route path="password" element={<RegisterPassword />} />
                      </Route>

                      {/* Forgot password */}
                      <Route path="forgot-password">
                        <Route index element={<ForgotPassword />} />
                        <Route path="reset" element={<ResetPassword />} />
                      </Route>

                      {/* Passwordless verification code */}
                      <Route path=":flow/verification-code" element={<VerificationCode />} />

                      {/* Mfa onboarding page. Prompt users to turn on 2-step verification. */}
                      <Route path="mfa-onboarding" element={<MfaOnboarding />} />

                      {/* Mfa binding */}
                      <Route path={UserMfaFlow.MfaBinding}>
                        <Route index element={<MfaBinding />} />
                        <Route path={MfaFactor.TOTP} element={<TotpBinding />} />
                        <Route path={MfaFactor.WebAuthn} element={<WebAuthnBinding />} />
                        <Route path={MfaFactor.BackupCode} element={<BackupCodeBinding />} />
                        <Route
                          path={MfaFactor.EmailVerificationCode}
                          element={<EmailMfaBinding />}
                        />
                        <Route
                          path={MfaFactor.PhoneVerificationCode}
                          element={<PhoneMfaBinding />}
                        />
                      </Route>

                      {/* Mfa verification */}
                      <Route path={UserMfaFlow.MfaVerification}>
                        <Route index element={<MfaVerification />} />
                        <Route path={MfaFactor.TOTP} element={<TotpVerification />} />
                        <Route path={MfaFactor.WebAuthn} element={<WebAuthnVerification />} />
                        <Route path={MfaFactor.BackupCode} element={<BackupCodeVerification />} />
                        <Route
                          path={MfaFactor.EmailVerificationCode}
                          element={<EmailVerificationCode />}
                        />
                        <Route
                          path={MfaFactor.PhoneVerificationCode}
                          element={<PhoneVerificationCode />}
                        />
                      </Route>

                      {/* Continue set up missing profile */}
                      <Route path="continue">
                        <Route path=":method" element={<Continue />} />
                      </Route>

                      {/* Social sign-in pages */}
                      <Route path="social">
                        <Route path="link/:connectorId" element={<SocialLinkAccount />} />
                        <Route path="landing/:connectorId" element={<SocialLanding />} />
                      </Route>

                      {/* Single sign-on */}
                      <Route path={experience.routes.sso}>
                        {/* Single sign-on first screen landing page */}
                        <Route index element={<SingleSignOnLanding />} />
                        <Route path="email" element={<SingleSignOnEmail />} />
                        <Route path="connectors" element={<SingleSignOnConnectors />} />
                      </Route>

                      {/* Consent */}
                      <Route path="consent" element={<Consent />} />

                      {/* Device flow */}
                      <Route path={experience.routes.device} element={<Device />} />
                      <Route
                        path={`${experience.routes.device}/success`}
                        element={<DeviceSuccess />}
                      />

                      {/*
                       * Identifier sign-in (first screen)
                       * The first screen which only display specific identifier-based sign-in methods to users
                       */}
                      <Route
                        path={experience.routes.identifierSignIn}
                        element={<IdentifierSignIn />}
                      />

                      {/*
                       * Identifier register (first screen)
                       * The first screen which only display specific identifier-based registration methods to users
                       */}
                      <Route
                        path={experience.routes.identifierRegister}
                        element={<IdentifierRegister />}
                      />

                      {/*
                       * Reset password (first screen)
                       * The first screen which allow users to directly access the password reset page
                       */}
                      <Route
                        path={experience.routes.resetPassword}
                        element={<ResetPasswordLanding />}
                      />
                      <Route path="*" element={<ErrorPage />} />
                    </Route>
                  </Route>
                </Routes>
              </AppBoundary>
            </CaptchaContextProvider>
          </UserInteractionContextProvider>
        </SettingsProvider>
      </PageContextProvider>
    </BrowserRouter>
  );
};

export default App;
